import * as yup from 'yup';
import React, { useContext, useEffect, useState } from 'react';
import MetaTags from 'react-meta-tags';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useParams } from 'react-router-dom';
import { Form, FormikHelpers, FormikValues } from 'formik';
import { ArnContext } from 'contexts';
import { IAuBreadcrumbItem, AuBreadcrumbs } from 'components/DTA';
import { ArnApplicationDvsMethodFormData, ArnApplicationFormData } from 'types/forms/arnApplicationTypes';
import { FormContext, FormContextProvider, IFormContextType } from 'contexts/FormContext';
import { FormStep, IFormAlertType } from 'types/forms/formTypes';
import PersonalDetails from './PersonalDetails';
import IdentityVerification from './IdentityVerification';
import IdentityDetails from './IdentityDetails';
import UploadFileForManualIdentification from './UploadFileForManualIdentification';
import UploadFileForNameChange from './UploadFileForNameChange';
import PreviousName from './PreviousName';
import NameChangeDocumentSelection from './NameChangeDocumentSelection';
import ManualPOIInformation from './ManualPOIInformation';
import ManualPOIPrimary from './ManualPOIPrimary';
import ManualPOISecondary from './ManualPOISecondary';
import ManualPOIDocumentUpload from './ManualPOIDocumentUpload';
import { FormAlert } from 'components/CASAForm';
import {
  DeclarationAndSubmit,
  AddAddressAndContact,
  FinalArnStep
} from '../Common';
import {
  personalDetailsValidationSchema,
  addressesAndContactDetailsValidationSchema,
  identityDetailsValidationSchema,
  previousNameChangeValidationSchema,
  changeOfNameValidationSchema
} from '../../validationSchemas/ValidationSchemas';
import { useArnService } from 'services';
import { FORM_ERROR_MESSAGES } from 'internalConstants';
import { IDENTITY_TYPES, NAME_CHANGE_DOC_TYPES } from 'arnConstants';
import { getPortalUrl } from 'components/Portal/Utilities';
import { urlSelector } from 'PortalStore/Selectors/EnvDataSelectors';
import { Loader } from 'components/common';
import { ExitModal } from 'components/CASAForm/ExitModal';
import { getIdentityDocumentTitleAndDataModel } from 'helpers/arnApplicationHelper';

interface UserDetails { userdetails: unknown; };
type ArnParams = {
  arnType: string;
};

const ArnApplicationDvsMethod: React.FC = () => {
  const { arnType } = useParams<ArnParams>();
  const { setArnType, hasArnOrPendingSubmissionResponse } = useContext(ArnContext);
  const userdetails = useSelector((state: UserDetails) => state.userdetails);
  const loginPath = useSelector((state: any) => state.B2CSettings.items.loginPath);
  const userDetailsValue: any = userdetails;
  const userEmail = userDetailsValue.items.value[0].emailaddress1;
  const B2CSettings = useSelector((state: any) => state.B2CSettings);
  const urlHome = useSelector((state: any) => urlSelector(state, 'Portal - Home URL'));
  const arnService = useArnService();
  const [isBackButtonClicked, setBackbuttonPress] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    window.history.pushState(window.location.search, '', window.location.pathname);
    window.addEventListener('popstate', onBackButtonEvent);
  }, []);

  const onBackButtonEvent = (e) => {
    e.preventDefault();
    if (!isBackButtonClicked) {
      setShowModal(true);
    }
  };

  const toggle = () => {
    setShowModal(!showModal);
    window.history.pushState(window.location.search, '', window.location.pathname);
    setBackbuttonPress(false);
  };

  const OK = () => {
    setShowModal(!showModal);
    setBackbuttonPress(true);
    window.history.back();
  };

  const exitModalParameters = {
    isOpen: showModal,
    toggleFunction: toggle,
    OKFunction: OK
  };


  if (loginPath != 'myCasa') {
    if (loginPath == 'myGov') {
      return arnType == 'new' ? <Redirect to='/arn/new' /> : <Redirect to='/arn/link' />;
    }
    return <Redirect to='/error' />;
  }

  useEffect(() => {
    if (hasArnOrPendingSubmissionResponse?.wasSuccessful && hasArnOrPendingSubmissionResponse.hasArnOrPendingSubmission) {
      const dashboardUrl = getPortalUrl('/dashind', B2CSettings.items.standalone, urlHome);
      window.location.href = dashboardUrl;
    }
  }, [hasArnOrPendingSubmissionResponse]);

  useEffect(() => {
    setArnType(arnType);
  }, [arnType]);

  const breadcrumbs: IAuBreadcrumbItem[] = [
    { link: '/', text: 'myCASA' },
    { text: arnType == 'new' ? 'Aviation reference number application' : 'Link aviation reference number application' }
  ];

  const initialValues: ArnApplicationDvsMethodFormData = {
    title: '',
    firstName: '',
    otherGivenNames: '',
    lastName: '',
    dateOfBirth: '',
    sex: '',
    nationality: 'Australian',
    email: userEmail,
    isPostalAddressDifferent: '',
    mobileNumber: '',
    identityType: '',
    declaration: '',
    isNamesMatch: true,
    address: {
      country: 'Australia',
      addressLookup: '',
      poBox: '',
      unitNumber: '',
      houseOrBuildingNumber: '',
      streetName: '',
      buildingName: '',
      cityOrSuburb: '',
      state: '',
      postcode: '',
      quickLookup: true
    },
    postalAddress: {
      country: 'Australia',
      addressLookup: '',
      poBox: '',
      unitNumber: '',
      houseOrBuildingNumber: '',
      streetName: '',
      buildingName: '',
      cityOrSuburb: '',
      state: '',
      postcode: '',
      quickLookup: true
    },
    auPassportDetails: {
      familyName: '',
      givenNames: '',
      dateOfBirth: '',
      passportNumber: '',
      declaration: '',
      dvsFailureCount: 0,
      dvsValidationIndicator: 'Not Validated'
    },
    auBirthCertificateDetails: {
      familyName: '',
      givenNames: '',
      dateOfBirth: '',
      stateOrTerritory: '',
      registrationNumber: '',
      dateOfRegistration: '',
      declaration: '',
      dvsFailureCount: 0,
      dvsValidationIndicator: 'Not Validated'
    },
    auCitizenshipCertificateDetails: {
      familyName: '',
      givenNames: '',
      dateOfBirth: '',
      acquisitionDate: '',
      certificateNumber: '',
      declaration: '',
      dvsFailureCount: 0,
      dvsValidationIndicator: 'Not Validated'
    },
    immiCardDetails: {
      familyName: '',
      givenNames: '',
      dateOfBirth: '',
      immiCardNumber: '',
      declaration: '',
      dvsFailureCount: 0,
      dvsValidationIndicator: 'Not Validated'
    },
    foreignPassportDetails: {
      isInAustralia: '',
      isAuPermanentResident: '',
      familyName: '',
      givenNames: '',
      dateOfBirth: '',
      passportNumber: '',
      nationalityOnPassport: '',
      declaration: '',
      dvsFailureCount: 0,
      dvsValidationIndicator: 'Not Validated'
    },
    previousName: {
      nameChangeAgree: '',
      changeOfNameDocument: ''
    },
    changeOfNameCertificateDetails: {
      familyName: '',
      givenNames: '',
      dateOfBirth: '',
      previousGivenNames: '',
      previousFamilyName: '',
      registrationNumber: '',
      registrationState: '',
      registrationDate: '',
      registrationYear: '',
      certificateNumber: '',
      certificateIssueDate: '',
      declaration: '',
      isManualUpload: false,
      dvsFailureCount: 0,
      dvsValidationIndicator: 'Not Validated',
      hasUploadedFiles: false,
      filename: []
    },
    marriageCertificateDetails: {
      firstPersonFamilyName: '',
      firstPersonGivenNames: '',
      secondPersonFamilyName: '',
      secondPersonGivenNames: '',
      dateOfMarriage: '',
      registrationState: '',
      registrationNumber: '',
      registrationDate: '',
      certificateNumber: '',
      certificateIssueDate: '',
      declaration: '',
      isManualUpload: false,
      dvsFailureCount: 0,
      dvsValidationIndicator: 'Not Validated',
      hasUploadedFiles: false,
      filename: []
    },
    divorcePapersDetails: {
      dvsFailureCount: 0,
      dvsValidationIndicator: 'Not Validated',
      isManualUpload: false,
      hasUploadedFiles: false,
      filename: []
    },
    manualPOIDocuments: {
      primaryDocuments70Points: [],
      primaryDocuments40Points: [],
      secondaryDocuments40Points: [],
      secondaryDocuments25Points: [],
      isManualUpload: false,
      hasUploadedFiles: false,
      filename: [],
      identityFiles: []
    }
  };

  const getIdentityFiles = (values: ArnApplicationDvsMethodFormData) => {
    return values.manualPOIDocuments.identityFiles;
  };

  const getNameChangeFiles = (values: ArnApplicationDvsMethodFormData) => {
    if (values?.previousName.changeOfNameDocument === NAME_CHANGE_DOC_TYPES.NAME_CHANGE_CERTIFICATE) {
      return values.changeOfNameCertificateDetails.identityFiles;
    }
    if (values?.previousName.changeOfNameDocument === NAME_CHANGE_DOC_TYPES.MARRIAGE_CERTIFICATE) {
      return values.marriageCertificateDetails.identityFiles;
    }
    if (values?.previousName.changeOfNameDocument === NAME_CHANGE_DOC_TYPES.DIVORCE_PAPERS) {
      return values.divorcePapersDetails.identityFiles;
    }
    return [];
  };

  const errorAlert: IFormAlertType = {
    as: 'error',
    title: 'There was an issue submitting your application',
    message: 'Please try again'
  };

  const getMappedFormValues = (values: ArnApplicationDvsMethodFormData) => {
    const {
      auPassportDetails,
      auBirthCertificateDetails,
      auCitizenshipCertificateDetails,
      immiCardDetails,
      foreignPassportDetails,
      changeOfNameCertificateDetails,
      marriageCertificateDetails,
      divorcePapersDetails,
      manualPOIDocuments,
      ...formValueObject
    } = values;

    // mapped identity details
    let formValuesWithIdentityDetails = {};
    if (values?.identityType === IDENTITY_TYPES.AU_PASSPORT) {
      formValuesWithIdentityDetails = { ...formValueObject, auPassportDetails };
    }
    else if (values?.identityType === IDENTITY_TYPES.AU_BIRTH_CERTIFICATE) {
      formValuesWithIdentityDetails = { ...formValueObject, auBirthCertificateDetails };
    }
    else if (values?.identityType === IDENTITY_TYPES.AU_CITIZENSHIP_CERTIFICATE) {
      formValuesWithIdentityDetails = { ...formValueObject, auCitizenshipCertificateDetails };
    }
    else if (values?.identityType === IDENTITY_TYPES.FORIGN_PASSPORT) {
      formValuesWithIdentityDetails = { ...formValueObject, foreignPassportDetails };
    }
    else if (values?.identityType === IDENTITY_TYPES.IMMI_CARD) {
      formValuesWithIdentityDetails = { ...formValueObject, immiCardDetails };
    }
    else if (values?.identityType === IDENTITY_TYPES.MANUAL_UPLOAD) {
      formValuesWithIdentityDetails = { ...formValueObject };
    }

    let mappedFormValues = { ...formValuesWithIdentityDetails };
    // mapped name changes details
    if (values?.previousName.changeOfNameDocument === NAME_CHANGE_DOC_TYPES.NAME_CHANGE_CERTIFICATE) {
      mappedFormValues = { ...formValuesWithIdentityDetails, changeOfNameCertificateDetails };
    }
    if (values?.previousName.changeOfNameDocument === NAME_CHANGE_DOC_TYPES.MARRIAGE_CERTIFICATE) {
      mappedFormValues = { ...formValuesWithIdentityDetails, marriageCertificateDetails };
    }
    if (values?.previousName.changeOfNameDocument === NAME_CHANGE_DOC_TYPES.DIVORCE_PAPERS) {
      mappedFormValues = { ...formValuesWithIdentityDetails, divorcePapersDetails };
    }
    return mappedFormValues;
  };

  const handleSubmit = async (values: ArnApplicationDvsMethodFormData, actions: FormikHelpers<ArnApplicationFormData & FormikValues>): Promise<IFormAlertType | null> => {
    const { setSubmitting } = actions;

    const arnApplicationPayLoad = {
      version: '01',
      name: 'ARN Application',
      rootFormCode: '',
      formCode: '',
      formData: getMappedFormValues(values)
    };

    const formDataObject = new FormData();
    formDataObject.append('request', JSON.stringify(arnApplicationPayLoad));
    const identityFilesData = getIdentityFiles(values);
    identityFilesData?.map(file => {
      formDataObject.append('files', file.file);
    });

    //add documents for name change
    const nameChangeFilesData = getNameChangeFiles(values);
    nameChangeFilesData?.map(file => {
      formDataObject.append('files', file.file);
    });

    try {
      // Send request to Submission service
      const response = arnType === 'new' ? await arnService.submitCreateMyCasaArnApplication(formDataObject)
        : await arnService.submitLinkMyCasaArnApplication(formDataObject);

      setSubmitting(false);
      // If request failed return error to the user
      if (!response || !response.wasSuccessful) {
        return errorAlert;
      }

      let dvsValidationIndicator = '';
      if (values?.manualPOIDocuments.isManualUpload && values?.manualPOIDocuments.hasUploadedFiles) {
        dvsValidationIndicator = 'Not Validated';
      }
      else {
        const identityType = getIdentityDocumentTitleAndDataModel(values.identityType);
        dvsValidationIndicator = values[`${identityType.identityType}`].dvsValidationIndicator;
      }

      // If successful return success to the user
      return dvsValidationIndicator === 'Passed' ? {
        as: 'success',
        title: arnType === 'new' ? 'Aviation reference number application successful'
          : 'Aviation reference number link successful'
      }
        : {
          as: 'success',
          title: arnType === 'new' ? 'Aviation reference number application received'
            : 'myCASA account created and Aviation reference link request received',
          message: arnType === 'new' ? 'We have received your application and documents' : null
        };
    }
    catch (error) {
      return errorAlert;
    }
  };

  const STEPS: FormStep[] = [
    {
      id: 'PersonalDetails',
      render: (props: any) => (
        <PersonalDetails {...props} />
      ),
      validationSchema: personalDetailsValidationSchema
    },
    {
      id: 'AddressAndContact',
      render: (props: any) => (
        <AddAddressAndContact nextStep='IdentityVerification' {...props} />
      ),
      validationSchema: addressesAndContactDetailsValidationSchema
    },
    {
      id: 'IdentityVerification',
      render: (props: any) => (
        <IdentityVerification nextStep='IdentificationDetails' {...props} />
      ),
      validationSchema: yup.object().shape({
        identityType: yup.string()
          .required(FORM_ERROR_MESSAGES.REQUIRED_OPTION)
      })
    },
    {
      id: 'ManualPOIInformation',
      render: (props: any) => (
        <ManualPOIInformation nextStep='ManualPOIPrimary' {...props} />
      )
    },
    {
      id: 'ManualPOIPrimary',
      render: (props: any) => (
        <ManualPOIPrimary nextStep='ManualPOISecondary' {...props} />
      )
    },
    {
      id: 'ManualPOISecondary',
      render: (props: any) => (
        <ManualPOISecondary nextStep='UploadFileForManualIdentification' {...props} />
      )
    },
    {
      id: 'UploadFileForManualIdentification',
      render: (props: any) => (
        <UploadFileForManualIdentification nextStep='DeclarationAndSubmit' {...props} />
      )
    },
    {
      id: 'IdentificationDetails',
      render: (props: any) => (
        <IdentityDetails nextStep='DeclarationAndSubmit' {...props} />
      ),
      validationSchema: identityDetailsValidationSchema
    },
    {
      id: 'PreviousName',
      render: (props: any) => (
        <PreviousName nextStep='NameChangeDetails' {...props} />
      ),
      validationSchema: previousNameChangeValidationSchema
    },
    {
      id: 'NameChangeDetails',
      render: (props: any) => (
        <NameChangeDocumentSelection nextStep='DeclarationAndSubmit' {...props} />
      ),
      validationSchema: changeOfNameValidationSchema
    },
    {
      id: 'UploadFileForManualNameChangeIdentification',
      render: (props: any) => (
        <UploadFileForNameChange nextStep='DeclarationAndSubmit' {...props} />
      ),
      validationSchema: changeOfNameValidationSchema
    },
    {
      id: 'DeclarationAndSubmit',
      render: () => (
        <DeclarationAndSubmit arnPath='dvs' arnType={arnType} alertTitle='Aviation reference number application successful' />
      ),
      validationSchema: yup.object().shape({
        declaration: yup.string()
          .required(FORM_ERROR_MESSAGES.DECLARATION)
      })
    },
    {
      id: 'Final',
      render: () => (
        <FinalArnStep arnPath='dvs' arnType={arnType} successful={true} />
      ),
      skipNavigation: true
    }
  ];

  if (!hasArnOrPendingSubmissionResponse
    || hasArnOrPendingSubmissionResponse.hasArnOrPendingSubmission === undefined
    || hasArnOrPendingSubmissionResponse.hasArnOrPendingSubmission === true
    || hasArnOrPendingSubmissionResponse.wasSuccessful === false) {
    return <Loader message='Working. Please wait.....' />;
  }

  return (
    <>
      <MetaTags>
        <title>{arnType == 'new' ? 'Create ARN' : 'Link ARN'}</title>
      </MetaTags>
      <AuBreadcrumbs label='breadcrumbs' items={breadcrumbs} />
      <FormContextProvider
        initialValues={initialValues}
        handleSubmit={handleSubmit}
        steps={STEPS}>
        <FormContext.Consumer>
          {({ currentStepIndex, steps, currentStepProps }: IFormContextType) => (
            <Form name='ARN Application'>
              <FormAlert htmlFor='ARN Application' />
              <ExitModal exitModalParams={exitModalParameters} />
              {steps[currentStepIndex].render(currentStepProps)}
            </Form>
          )}
        </FormContext.Consumer>
      </FormContextProvider>
    </>
  );
};

export default ArnApplicationDvsMethod;